import { useEffect, useCallback } from 'react';
import { useSocket } from './useSocket';
import { useNotificationStore } from '@/store/notificationStore';
import type { GlobalNotification, NotificationCategory } from '@/types/notification.types';

/**
 * Hook to sync real-time notifications from server
 */
export function useNotifications() {
  const socket = useSocket('client');
  const friendsSocket = useSocket('friends');
  const { addNotification, preferences } = useNotificationStore();

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!preferences.enableSounds) return;

    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(e => console.warn('Could not play notification sound:', e));
    } catch (error) {
      console.warn('Notification sound error:', error);
    }
  }, [preferences.enableSounds]);

  // Show toast notification
  const showToast = useCallback((notification: Omit<GlobalNotification, 'id' | 'timestamp'>) => {
    if (!preferences.enableToasts) return;
    if (!preferences.categories[notification.category as NotificationCategory]) return;

    addNotification(notification);
    playNotificationSound();
  }, [preferences, addNotification, playNotificationSound]);

  // Listen for notifications from both namespaces
  useEffect(() => {
    if (!socket && !friendsSocket) return;

    const handleNotification = (notification: any) => {
      console.log('[useNotifications] Received notification:', notification);

      showToast({
        category: notification.category || 'system',
        type: notification.type,
        title: notification.title,
        message: notification.message,
        priority: notification.priority || 'normal',
        read: false,
        actionUrl: notification.actionUrl,
        actionLabel: notification.actionLabel
      });
    };

    // Listen on both sockets
    socket?.on('notification:new', handleNotification);
    friendsSocket?.on('notification:new', handleNotification);

    return () => {
      socket?.off('notification:new', handleNotification);
      friendsSocket?.off('notification:new', handleNotification);
    };
  }, [socket, friendsSocket, showToast]);

  return {
    showToast
  };
}
